import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion,AccordionDetails,AccordionSummary,Autocomplete,Button,Card,DialogActions,IconButton,Skeleton,Stack,TextField,Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useQuery } from '@tanstack/react-query';
import { useContext,useEffect,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../context/UserContext';
import { WebFormContext } from '../../context/WebFormContext';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import useExecutionLease from '../../hooks/useExecutionLease';
import Clients from '../../service/Clients';
import Execucoes,{ EXECUTION_LEASE_SESSION_KEY } from '../../service/Execucoes';
import queryClient from '../../service/query';
import { hierarchyType } from '../../types/new-form-filling/types';
import { executaForm } from '../helpers/checklists';
import NewFormFilling,{ ExecutionContext } from '../NewFormFilling/Structure/NewFormFilling';

const clientsService = new Clients();
const execucoesService = new Execucoes();

type props = {
  formId: string,
  open: boolean,
  setOpen: Function,
  executionContext?: ExecutionContext | null,
  onPausedExecutionsChanged?: () => void,
  onLeaseLost?: () => void,
};

export default function FormFillingModal({ formId, open, setOpen, executionContext, onPausedExecutionsChanged, onLeaseLost }: props) {
  const {
    hierarchyLabel,
    selectedCliente,
    setSelectedCliente,
    selectedLocal,
    setSelectedLocal,
    selectedConjunto,
    setSelectedConjunto,
    selectedUnidade,
    setSelectedUnidade,
    userData,
  } = useContext(UserContext);
  const { setWebForm, webForm, fotos, arquivos } = useContext(WebFormContext);
  const [executionStart, setExecutionStart] = useState<number | undefined>(executionContext?.inicio);
  const [isStarting, setIsStarting] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const isResuming = Boolean(executionContext?.isResuming);
  const resumingItens = executionContext?.checklist?.itens;
  const resumeError = executionContext?.resumeError || (
    isResuming && (!Array.isArray(resumingItens) || resumingItens.length === 0)
      ? 'Não foi possível retomar a execução porque o formulário não possui itens para preencher.'
      : undefined
  );
  const [leaseActive, setLeaseActive] = useState(true);
  const [expirationCheck, setExpirationCheck] = useState(Date.now());
  const executionExpiresAt = executionContext?.execExpiraEm ?? executionContext?.checklist?.execExpiraEm;
  const isExpiredResumedExecution = () => Boolean(
    isResuming && executionExpiresAt != null && executionExpiresAt <= Date.now(),
  );
  const executionExpired = Boolean(
    isResuming && executionExpiresAt != null && executionExpiresAt <= expirationCheck,
  );
  const clearLeaseSession = () => sessionStorage.removeItem(EXECUTION_LEASE_SESSION_KEY);
  const { release } = useExecutionLease({
    enabled: open && isResuming && leaseActive && !resumeError,
    inicio: executionStart,
    onLeaseLost: () => {
      clearLeaseSession();
      onPausedExecutionsChanged?.();
      onLeaseLost?.();
    },
  });

  useEffect(() => {
    if (!open) {
      setExecutionStart(undefined);
      setIsStarting(false);
      setLeaseActive(true);
    }
  }, [open]);

  useEffect(() => {
    setExpirationCheck(Date.now());
    if (!isResuming || executionExpiresAt == null) return;

    const timeout = window.setTimeout(
      () => setExpirationCheck(Date.now()),
      Math.max(0, executionExpiresAt - Date.now()) + 1,
    );
    return () => window.clearTimeout(timeout);
  }, [executionExpiresAt, isResuming]);

  const { t } = useTranslation('translation');

  const { data, isLoading, isFetching, isSuccess, isError, error } = useQuery({
    queryKey: ['GET_HIERARCHY_LIST'],
    queryFn: async () => {
      toast.info(t('Filling.formFilling.fetchingGeographicHierarchy'), { toastId: 'buscando' });
      const token = await AttToken();
      if (token) {
        const response: hierarchyType[] = await clientsService.getListForWebForm(token, formId as string);
        return response;
      }
    },
    enabled: !executionContext?.isResuming,
    retry: 3,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (isSuccess && data?.length) {
      trackEventMatomo('preenchimento', 'sucesso', 'carregamento', 'hierarquia geográfica carregada');
      toast.success(t('Filling.formFilling.hierarchyFound'), { toastId: 'sucesso' });
    } if (isError && error.message !== "Request failed with status code 404") {
      trackEventMatomo('preenchimento', 'erro', 'carregamento', 'falha ao carregar hierarquia geográfica');
      toast.error(t('Filling.formFilling.errorFetchingHierarchy'), { toastId: 'erro' });
    } if (error?.message === "Request failed with status code 404") {
      toast.info(t('Filling.formFilling.formNoHierarchyAssociated'), { toastId: 'semHier' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isSuccess, data, error]);

  const loading = isLoading || isFetching;

  useEffect(() => {
    if (!selectedCliente) {
      setSelectedLocal(null);
      setSelectedConjunto(null);
      setSelectedUnidade(null);
    } if (!selectedLocal) {
      setSelectedConjunto(null);
      setSelectedUnidade(null);
    } if (!selectedConjunto) {
      setSelectedUnidade(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCliente, selectedLocal, selectedConjunto, selectedUnidade]);

  const finishClose = (releaseLease = true) => {
    if (isResuming) {
      if (releaseLease) void release();
      setLeaseActive(false);
      clearLeaseSession();
      onPausedExecutionsChanged?.();
    }
    setOpen(false);
    setWebForm(null);
    setSelectedCliente(null);
    setSelectedLocal(null);
    setSelectedConjunto(null);
    setSelectedUnidade(null);
    queryClient.removeQueries({ queryKey: ['GET_HIERARCHY_LIST'] });
    queryClient.removeQueries({ queryKey: ['GET_FORM_DATA'] });
  };

  const close = (finished = false) => {
    if (resumeError) {
      finishClose();
      return;
    }
    if (executionStart && !finished) {
      trackEventMatomo('preenchimento', 'click', 'modal', 'solicita encerramento do preenchimento');
      setConfirmCloseOpen(true);
      return;
    }
    if (finished && executionContext?.isResuming) {
      onPausedExecutionsChanged?.();
    }
    finishClose(!finished);
  };

  const pauseExecution = async () => {
    if (!executionStart || !webForm || isPausing || isExpiredResumedExecution()) {
      if (isExpiredResumedExecution()) {
        toast.warning('Esta execução expirou. Finalize ou cancele o formulário.', { toastId: 'expired-execution-cannot-pause' });
      }
      return;
    }

    trackEventMatomo('preenchimento', 'click', 'botão', 'pausa preenchimento');
    setIsPausing(true);
    try {
      await executaForm(
        executionStart,
        webForm,
        true,
        [...fotos, ...arquivos],
        selectedCliente,
        selectedConjunto,
        selectedLocal,
        selectedUnidade,
        userData ?? '',
        undefined,
        true,
        isResuming ? true : undefined,
      );
      setLeaseActive(false);
      clearLeaseSession();
      onPausedExecutionsChanged?.();
      setConfirmCloseOpen(false);
      finishClose(false);
    } finally {
      setIsPausing(false);
    }
  };

  const cancelExecution = async () => {
    if (!executionContext?.isResuming) {
      trackEventMatomo('preenchimento', 'click', 'botão', 'cancela preenchimento');
      finishClose();
      return;
    }

    const checklist = webForm ?? executionContext.checklist;
    if (!executionStart || !checklist || isCancelling) return;

    const token = await AttToken();
    if (!token) return;

    trackEventMatomo('preenchimento', 'click', 'botão', 'desiste preenchimento pausado');
    setIsCancelling(true);
    try {
      await execucoesService.executaFormV3(token, {
        ...checklist,
        inicio: executionStart,
        fim: Date.now(),
        pausa: false,
        pausaExpiraEm: null,
        expiraEm: null,
        ultimaDataPausa: null,
        status: false,
      }, { executionLease: true });
      setLeaseActive(false);
      clearLeaseSession();
      onPausedExecutionsChanged?.();
      setConfirmCloseOpen(false);
      finishClose(false);
    } finally {
      setIsCancelling(false);
    }
  };

  const startExecution = async () => {
    if (isStarting || executionStart) return;

    const token = await AttToken();
    if (!token) return;

    const inicio = Date.now();
    trackEventMatomo('preenchimento', 'click', 'botão', 'inicia formulário');
    setIsStarting(true);
    try {
      const response = await execucoesService.controlarExecucao(token, inicio);
      if (!response.success) {
        trackEventMatomo('preenchimento', 'erro', 'início', 'falha ao iniciar formulário');
        toast.error(response.data.message, { toastId: response.data.message });
        return;
      }
      setExecutionStart(inicio);
      trackEventMatomo('preenchimento', 'sucesso', 'início', 'formulário iniciado');
    } finally {
      setIsStarting(false);
    }
  };


  const HierarchyAutocomplete = ({
    data,
    noOptionsLabel,
    loadingText,
    setValue,
    label,
    value,
  }: {
    data: { id: string, nome: string }[],
    noOptionsLabel: string,
    loadingText: string,
    setValue: Function,
    label: string,
    value: { nome: string, id: string } | null,
  }) => {
    return (
      <Autocomplete
        disabled={!data?.length}
        fullWidth
        getOptionLabel={option => option.nome}
        getOptionKey={option => option.id}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={noOptionsLabel}
        loading
        value={value || null}
        loadingText={loadingText}
        filterSelectedOptions
        options={data
          ?.sort((a: { nome: string, id: string }, b: { nome: string, id: string }) => { return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : - 1; })
          ?.map((ele: { nome: string, id: string }) => ({
            ...ele
          })) || []}
        onChange={(_event, newValue: { nome: string, id: string } | null) => {
          if (newValue) trackEventMatomo('preenchimento', 'click', 'select', `seleciona ${label}`);
          setValue(newValue);
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <span>{option.nome}</span>
          </li>)}
        renderInput={(params) => <TextField {...params} label={label} />}
      />
    );
  };

  return (
    <Dialog
      sx={{ zIndex: 1 }}
      fullWidth
      maxWidth='md'
      open={open}
      onClose={() => close()}
      scroll='paper'
    >
      <DialogTitle id="scroll-dialog-title">{t('Filling.fillForm')}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => close()}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        <Stack
          width='100%'
          direction="column"
          justifyContent="flex-start"
          alignItems='center'
          spacing={2}
        >

          {resumeError && <Card sx={{ width: '100%', p: 2, border: '1px solid', borderColor: 'error.main' }}>
            <Typography color='error' fontWeight={700} textAlign='center'>
              Houve um erro ao retomar a execução.
            </Typography>
            <Typography color='text.secondary' textAlign='center' sx={{ mt: 1 }}>
              {resumeError}
            </Typography>
            <Stack direction='row' justifyContent='center' sx={{ mt: 2 }}>
              <Button variant='contained' onClick={() => finishClose()}>Fechar</Button>
            </Stack>
          </Card>}

          {!resumeError && !executionContext?.isResuming && <>
          {data?.length && <Typography variant='subtitle1' color='textDisabled'>
            {t('Filling.fillSubitens.selectGeographicHierarchy').slice(0, -1)}
          </Typography>}
          {loading
            ? <Skeleton width='100%' variant='rounded' height={80} />
            : <Card
              sx={{
                padding: 2,
                backgroundColor: !selectedUnidade ? '#F6D265' : '#31AD21',
              }}
            >
              <Typography fontWeight={600} textAlign='center' sx={{ color: !selectedUnidade ? 'black' : 'white' }}>
                {!data
                  ? `${t('Filling.fillSubitens.formNotAssociatedWithHierarchy')}, ou este usuário não está associado a equipe vinculada a este formulário.`
                  : !selectedUnidade
                    ? `${t('Filling.fillSubitens.formExecutionRequiresHierarchy')}`
                    : `${t('Filling.fillSubitens.hierarchySelected')}`}
              </Typography>
            </Card>}
          {data?.length &&
            <Accordion sx={{ width: '100%' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
              >
                <Typography color='textDisabled' textAlign='center' fontWeight={700}>{t('Filling.formFilling.geographicHierarchy')}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack direction='column' spacing={1.5} justifyContent='flex-start' alignItems='flex-start'>
                  <HierarchyAutocomplete
                    value={selectedCliente}
                    setValue={setSelectedCliente}
                    data={data ?? []}
                    noOptionsLabel={`${t('Filling.fillSubitens.without')} ${hierarchyLabel.cliente} ${t('Filling.fillSubitens.associated')}`}
                    label={hierarchyLabel.cliente}
                    loadingText={`${t('Filling.fillSubitens.searching')} ${hierarchyLabel.cliente}...`}
                  />
                  {selectedCliente
                    && <HierarchyAutocomplete
                      value={selectedLocal}
                      setValue={setSelectedLocal}
                      data={data?.find((ele: { nome: string, id: string }) => ele.id === selectedCliente?.id)?.locaisList ?? []}
                      noOptionsLabel={`${t('Filling.fillSubitens.without')} ${hierarchyLabel.local} ${t('Filling.fillSubitens.associated')}`}
                      label={hierarchyLabel.local}
                      loadingText={`${t('Filling.fillSubitens.searching')} ${hierarchyLabel.local}...`}
                    />}
                  {selectedCliente && selectedLocal
                    && <HierarchyAutocomplete
                      value={selectedConjunto}
                      setValue={setSelectedConjunto}
                      data={data
                        ?.find((ele: { nome: string, id: string }) => ele.id === selectedCliente?.id)?.locaisList
                        ?.find((el: { nome: string, id: string }) => el?.id === selectedLocal?.id)?.conjuntosList ?? []}
                      noOptionsLabel={`${t('Filling.fillSubitens.without')} ${hierarchyLabel.conjunto} ${t('Filling.fillSubitens.associated')}`}
                      label={hierarchyLabel.conjunto}
                      loadingText={`${t('Filling.fillSubitens.searching')} ${hierarchyLabel.conjunto}...`}
                    />}
                  {selectedCliente && selectedLocal && selectedConjunto
                    && <HierarchyAutocomplete
                      value={selectedUnidade}
                      setValue={setSelectedUnidade}
                      data={data
                        ?.find((ele: { nome: string, id: string }) => ele.id === selectedCliente?.id)?.locaisList
                        ?.find((el: { nome: string, id: string }) => el?.id === selectedLocal?.id)?.conjuntosList
                        ?.find((elem: { nome: string, id: string }) => elem.id === selectedConjunto?.id)?.unidadesList ?? []}
                      noOptionsLabel={`${t('Filling.fillSubitens.without')} ${hierarchyLabel.unidade} ${t('Filling.fillSubitens.associated')}`}
                      label={hierarchyLabel.unidade}
                      loadingText={`${t('Filling.fillSubitens.searching')} ${hierarchyLabel.unidade}...`}
                    />}
                </Stack>
              </AccordionDetails>
            </Accordion>}
          {(selectedCliente?.id && selectedLocal?.id && selectedConjunto?.id && selectedUnidade?.id) && !executionStart &&
            <Button variant='contained' onClick={startExecution} disabled={isStarting}>
              {isStarting ? 'Iniciando...' : 'Iniciar formulário'}
            </Button>}
          </>}
          {!resumeError && executionStart && open && <NewFormFilling
            id={formId}
            close={close}
            executionContext={executionContext ?? { inicio: executionStart, isResuming: false }}
            onExecutionFinished={() => {
              setLeaseActive(false);
              clearLeaseSession();
              onPausedExecutionsChanged?.();
            }}
            onPause={executionExpired ? undefined : () => void pauseExecution()}
            onCancel={executionContext?.isResuming ? () => setConfirmCloseOpen(true) : undefined}
            isPausing={isPausing}
          />}
        </Stack>
      </DialogContent>
      <Dialog open={confirmCloseOpen} onClose={() => !executionExpired && !isPausing && !isCancelling && setConfirmCloseOpen(false)}>
        <DialogTitle>{executionExpired ? 'Resolver execução expirada' : 'Pausar preenchimento?'}</DialogTitle>
        <DialogContent>
          <Typography>{executionExpired
            ? 'Esta execução expirou. Finalize o formulário ou desista deste preenchimento para continuar.'
            : 'Deseja pausar o formulário para retomar depois ou desistir deste preenchimento?'}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            trackEventMatomo('preenchimento', 'click', 'botão', 'continua preenchimento');
            setConfirmCloseOpen(false);
          }} disabled={isPausing || isCancelling}>Continuar preenchendo</Button>
          <Button onClick={cancelExecution} disabled={isPausing || isCancelling}>{isCancelling ? 'Cancelando...' : 'Desistir'}</Button>
          {!executionExpired && <Button variant='contained' onClick={pauseExecution} disabled={isPausing || isCancelling}>{isPausing ? 'Pausando...' : 'Pausar'}</Button>}
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
